import { useEffect, useRef } from "react";

const DEFAULT_IDLE_MS = 10 * 60 * 60 * 1000; // 10 hours
/** Throttle how often mousemove resets the idle timer (mousemove can fire very frequently). */
const MOUSEMOVE_SCHEDULE_THROTTLE_MS = 2000;

const clearSessionAndRedirect = () => {
    try {
        localStorage.clear();
    } catch (e) {
        // ignore
    }
    window.location.href = "/login";
};

/**
 * Ends the session (clears storage, redirects to login) if there is no mouse movement
 * for `idleMs`. Any `mousemove` resets the countdown.
 * Only runs while `authUser` is present in localStorage.
 */
export function useIdleMouseSessionTimeout(idleMs = DEFAULT_IDLE_MS) {
    const timeoutRef = useRef(null);
    const lastThrottleRef = useRef(0);

    useEffect(() => {
        if (!localStorage.getItem("authUser")) {
            return undefined;
        }

        const clearTimer = () => {
            if (timeoutRef.current != null) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };

        const scheduleLogout = () => {
            clearTimer();
            timeoutRef.current = setTimeout(() => {
                clearSessionAndRedirect();
            }, idleMs);
        };

        scheduleLogout();

        const onMouseMove = () => {
            const now = Date.now();
            if (now - lastThrottleRef.current < MOUSEMOVE_SCHEDULE_THROTTLE_MS) {
                return;
            }
            lastThrottleRef.current = now;
            scheduleLogout();
        };

        window.addEventListener("mousemove", onMouseMove, { passive: true });

        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            clearTimer();
        };
    }, [idleMs]);
}
